/**
 * Catálogo Oficial de Peças OEM (EPC) — Honda ADV 150 (2023/2024 - Projeto K0W / Bloco KF43E)
 * Padrão Concessionária Honda Dream & Filosofia Fábio Akita (Zero Mocks & Fail-Visible)
 */

export type CatalogSubsystemId =
  | "motor"
  | "cvt"
  | "pgmfi"
  | "chassi"
  | "freios"
  | "suspensao"
  | "eletrica"
  | "consumiveis";

export interface CatalogSubsystemMeta {
  id: CatalogSubsystemId;
  name: string;
  icon: string;
  description: string;
  badgeColor: string;
}

export interface CatalogPartItem {
  id: string;
  partNumber: string;
  name: string;
  nameEn?: string;
  subsystem: CatalogSubsystemId;
  categoryLabel: string;
  hotspotId: string;
  explodedViewId?: string;
  calloutBalloonNumber?: number;
  description: string;
  nominalDimensionMm?: number;
  serviceLimitMm?: number;
  torqueSpec?: string;
  requiredTool?: string;
  criticalGotcha: string;
  isWearItem: boolean;
}

export const ADV150_SUBSYSTEMS: ReadonlyArray<CatalogSubsystemMeta> = [
  {
    id: "cvt",
    name: "Transmissão CVT V-Matic",
    icon: "⚙️",
    description:
      "Variador primário, roletes 21g a seco, correia Bando Kevlar, polia secundária e redutor final.",
    badgeColor: "#00F2FE",
  },
  {
    id: "motor",
    name: "Motor eSP 149,3 cm³",
    icon: "🔩",
    description:
      "Cárter KF43E, cabeçote OHC, válvulas <35°C, vela NGK MR8K-9, bomba d'água e radiador lateral.",
    badgeColor: "#10B981",
  },
  {
    id: "pgmfi",
    name: "Injeção PGM-FI & Eletrônica",
    icon: "⚡",
    description:
      "TBI Keihin 26mm com MoS2, bico injetor 6 furos, sensores integrados e tomada DLC de diagnóstico.",
    badgeColor: "#38BDF8",
  },
  {
    id: "freios",
    name: "Freios Nissin & ABS",
    icon: "🛑",
    description:
      "Discos Wave 240/220mm, cálipers Nissin dianteiro/traseiro, pastilhas sinterizadas e modulador ABS.",
    badgeColor: "#EF4444",
  },
  {
    id: "suspensao",
    name: "Ciclística & Suspensão Showa",
    icon: "🏍️",
    description:
      "Garfo dianteiro Showa 31mm (130mm curso) e amortecedores duplos traseiros Showa Piggyback a gás (120mm).",
    badgeColor: "#F59E0B",
  },
  {
    id: "chassi",
    name: "Chassi, Baú & Carenagens",
    icon: "🛡️",
    description:
      "Quadro monobloco berço duplo, baú 27L, assoalho, balança de alumínio e presilhas push-pins 6mm.",
    badgeColor: "#8B5CF6",
  },
  {
    id: "eletrica",
    name: "Elétrica, Iluminação & Smart Key",
    icon: "💡",
    description:
      "Bateria selada YTZ6V 12V 5Ah, transmissor Smart Key CR2032, estator ACG e faróis Full LED.",
    badgeColor: "#EC4899",
  },
  {
    id: "consumiveis",
    name: "Consumíveis & Ferramental",
    icon: "🔧",
    description:
      "Óleo Pro Honda 10W-30 MB, Coolant OAT 50/50, chave JIS Vessel #2, fixador em Y e compressor de mola.",
    badgeColor: "#64748B",
  },
];

export const ADV150_CANONICAL_PARTS: ReadonlyArray<CatalogPartItem> = [
  // ==========================================
  // GRUPO 1: TRANSMISSÃO CVT V-MATIC
  // ==========================================
  {
    id: "oem-correia-23100",
    partNumber: "23100-K97-T01",
    name: "Correia Dentada de Transmissão V-Belt (Bando Kevlar)",
    nameEn: "Drive Belt (Bando / Honda)",
    subsystem: "cvt",
    categoryLabel: "Transmissão Primária/Secundária",
    hotspotId: "hotspot-cvt-correia",
    explodedViewId: "cvt-variador-polias-explodida.svg",
    calloutBalloonNumber: 6,
    description:
      "Correia de transmissão contínua com cordonéis em fibra de Kevlar/aramida com perfil de 26 graus.",
    nominalDimensionMm: 19.5,
    serviceLimitMm: 18.5,
    torqueSpec: "Protocolo Belt Sinking",
    requiredTool: "Paquímetro digital centesimal (DIN 862)",
    criticalGotcha:
      "GOTCHA FATAL: A correia DEVE estar afundada na secundária durante o aperto dianteiro. Se mordida, cria falso torque e quebra a ponta do virabrequim.",
    isWearItem: true,
  },
  {
    id: "oem-roletes-22123",
    partNumber: "22123-K0W-B00",
    name: "Jogo de Roletes Centrífugos do Variador (6 un x 21g)",
    nameEn: "Weight Roller Set (21g)",
    subsystem: "cvt",
    categoryLabel: "Variador Primário",
    hotspotId: "hotspot-cvt-primario",
    explodedViewId: "cvt-variador-polias-explodida.svg",
    calloutBalloonNumber: 3,
    description:
      "Conjunto de 6 roletes centrífugos balanceados de 21,0 gramas cada com núcleo de latão e capa polimérica.",
    nominalDimensionMm: 20.0,
    serviceLimitMm: 19.5,
    torqueSpec: "Alojados a seco",
    requiredTool: "Paquímetro centesimal ou balança de precisão 0,1g",
    criticalGotcha:
      "REGRA MANDATÓRIA: MONTAGEM RIGOROSAMENTE A SECO. NUNCA aplique graxa ou lubrificante nas pistas dos roletes.",
    isWearItem: true,
  },
  {
    id: "oem-face-movel-22110",
    partNumber: "22110-K0W-B00",
    name: "Face Móvel da Polia Primária (Movable Drive Face)",
    nameEn: "Movable Drive Face Comp.",
    subsystem: "cvt",
    categoryLabel: "Variador Primário",
    hotspotId: "hotspot-cvt-primario",
    explodedViewId: "cvt-variador-polias-explodida.svg",
    calloutBalloonNumber: 4,
    description:
      "Polia cônica dianteira móvel usinada em alumínio naval com 6 pistas e bucha interna deslizante.",
    nominalDimensionMm: 24.0,
    serviceLimitMm: 24.06,
    torqueSpec: "Deslizamento axial livre",
    requiredTool: "Súbito / Micrômetro interno",
    criticalGotcha:
      "Substituir imediatamente se houver ondulações ou degraus na face cônica de contato com a correia.",
    isWearItem: false,
  },
  {
    id: "oem-bucha-primaria-22105",
    partNumber: "22105-K97-T00",
    name: "Bucha Central Deslizante da Polia Primária (Boss)",
    nameEn: "Drive Face Boss",
    subsystem: "cvt",
    categoryLabel: "Variador Primário",
    hotspotId: "hotspot-cvt-primario",
    explodedViewId: "cvt-variador-polias-explodida.svg",
    calloutBalloonNumber: 5,
    description:
      "Bucha tubular de aço temperado e retificado para deslizamento longitudinal da face móvel.",
    nominalDimensionMm: 23.98,
    serviceLimitMm: 23.96,
    torqueSpec: "Batente mecânico no eixo",
    requiredTool: "Micrômetro externo 25-50 mm",
    criticalGotcha:
      "Diâmetro externo mínimo: 23,960 mm. Folga radial máxima com a polia móvel de 0,10 mm.",
    isWearItem: false,
  },
  {
    id: "oem-cursores-22011",
    partNumber: "22011-K97-T00",
    name: "Cursores Plásticos da Placa de Rampa (Slide Pieces 3 un)",
    nameEn: "Slide Piece Set (3 pcs)",
    subsystem: "cvt",
    categoryLabel: "Variador Primário",
    hotspotId: "hotspot-cvt-primario",
    explodedViewId: "cvt-variador-polias-explodida.svg",
    calloutBalloonNumber: 2,
    description:
      "Guias deslizantes em polímero técnico montadas nas aletas da rampa de aço do variador.",
    nominalDimensionMm: 0.0,
    serviceLimitMm: 0.2,
    torqueSpec: "Encaixe sob pressão",
    requiredTool: "Inspeção tátil de folga lateral",
    criticalGotcha:
      "Folga lateral nos cursores causa ruído de chocalho metálico na marcha lenta e desgaste precoce das aletas.",
    isWearItem: true,
  },
  {
    id: "oem-face-fixa-22102",
    partNumber: "22102-K97-T01",
    name: "Face Fixa Ventilada da Polia Primária (Drive Face)",
    nameEn: "Drive Face (Fan Blade)",
    subsystem: "cvt",
    categoryLabel: "Variador Primário",
    hotspotId: "hotspot-cvt-primario",
    explodedViewId: "cvt-variador-polias-explodida.svg",
    calloutBalloonNumber: 8,
    description:
      "Polia dianteira externa com aletas radiais de ventilação centrífuga e centro estriado para virabrequim M14.",
    torqueSpec: "59 N.m (6,0 kgf.m)",
    requiredTool: "Universal Holder Honda 07725-0030000",
    criticalGotcha:
      "PROIBIDO TRAVAR NAS ALETAS! Apoiar a ferramenta exclusivamente nos furos usinados reforçados para não partir as pás.",
    isWearItem: false,
  },
  {
    id: "oem-arruela-belleville-90441",
    partNumber: "90441-K97-T00",
    name: "Arruela Cônica Elástica Belleville do Virabrequim",
    nameEn: "Conical Spring Washer 14mm",
    subsystem: "cvt",
    categoryLabel: "Variador Primário",
    hotspotId: "hotspot-cvt-primario",
    explodedViewId: "cvt-variador-polias-explodida.svg",
    calloutBalloonNumber: 9,
    description:
      "Arruela mola de aço especial estampada com concavidade calibrada para pré-carga da porca M14.",
    torqueSpec: "Sob compressão de 59 N.m",
    requiredTool: "Inspeção visual da gravação",
    criticalGotcha:
      "ORIENTAÇÃO OBRIGATÓRIA: A face com a inscrição 'OUTSIDE' DEVE estar voltada para fora (apoiando sob a porca).",
    isWearItem: false,
  },
  {
    id: "oem-porca-virabrequim-90201",
    partNumber: "90201-415-000",
    name: "Porca Flangeada M14 do Virabrequim (Sextavado 22 mm)",
    nameEn: "Flange Nut M14 x 1.25",
    subsystem: "cvt",
    categoryLabel: "Variador Primário",
    hotspotId: "hotspot-cvt-primario",
    explodedViewId: "cvt-variador-polias-explodida.svg",
    calloutBalloonNumber: 10,
    description:
      "Porca de retenção principal de alta resistência Classe 10.9 com rosca métrica fina M14 x 1,25.",
    torqueSpec: "59 N.m (6,0 kgf.m)",
    requiredTool: "Torquímetro de estalo calibrado + Soquete 22 mm sextavado",
    criticalGotcha:
      "Aperto proibido com pistola pneumática descalibrada. Aplicar 59 N.m estritos com chave em Y nos orifícios.",
    isWearItem: false,
  },
  {
    id: "oem-campana-22100",
    partNumber: "22100-K0W-N00",
    name: "Campana da Embreagem Secundária (Clutch Outer 125 mm)",
    nameEn: "Clutch Outer Comp.",
    subsystem: "cvt",
    categoryLabel: "Embreagem Secundária",
    hotspotId: "hotspot-cvt-secundario",
    explodedViewId: "cvt-secundaria-embreagem-explodida.svg",
    calloutBalloonNumber: 1,
    description:
      "Campana de fricção em aço estampado retificada internamente com diâmetro nominal de 125,0 mm.",
    nominalDimensionMm: 125.0,
    serviceLimitMm: 125.5,
    torqueSpec: "49 N.m (5,0 kgf.m)",
    requiredTool: "Paquímetro interno / Fixador de pinos",
    criticalGotcha:
      "Substituir se o diâmetro ultrapassar 125,5 mm ou apresentar coloração azulada/ranhuras profundas.",
    isWearItem: false,
  },
  {
    id: "oem-porca-campana-90202",
    partNumber: "90202-KN7-670",
    name: "Porca Flangeada M12 da Campana de Embreagem (Sext. 19 mm)",
    nameEn: "Flange Nut M12 x 1.25",
    subsystem: "cvt",
    categoryLabel: "Embreagem Secundária",
    hotspotId: "hotspot-cvt-secundario",
    explodedViewId: "cvt-secundaria-embreagem-explodida.svg",
    calloutBalloonNumber: 2,
    description: "Porca sextavada de 19 mm para retenção da campana no eixo primário do redutor.",
    torqueSpec: "49 N.m (5,0 kgf.m)",
    requiredTool: "Torquímetro + Soquete 19 mm + Fixador universal",
    criticalGotcha: "Imobilizar a campana com ferramenta de pinos; não usar talhadeira.",
    isWearItem: false,
  },
  {
    id: "oem-sapatas-22535",
    partNumber: "22535-K0W-N00",
    name: "Conjunto de Sapatas da Embreagem Centrífuga (3 un)",
    nameEn: "Clutch Weight Set (3 Shoes)",
    subsystem: "cvt",
    categoryLabel: "Embreagem Secundária",
    hotspotId: "hotspot-cvt-secundario",
    explodedViewId: "cvt-secundaria-embreagem-explodida.svg",
    calloutBalloonNumber: 3,
    description:
      "Prato com 3 sapatas de atrito com molas de acoplamento calibradas para ~2.200 rpm.",
    nominalDimensionMm: 4.5,
    serviceLimitMm: 2.0,
    torqueSpec: "Acoplamento centrífugo",
    requiredTool: "Paquímetro digital (medir material de fricção)",
    criticalGotcha:
      "Substituir quando a lona atingir 2,0 mm. Desvitrificar levemente com lixa d'água 220 em cruz a cada revisão.",
    isWearItem: true,
  },
  {
    id: "oem-contra-mola-23233",
    partNumber: "23233-K97-T00",
    name: "Contra-Mola Helicoidal da Polia Secundária",
    nameEn: "Driven Face Spring",
    subsystem: "cvt",
    categoryLabel: "Polia Secundária",
    hotspotId: "hotspot-cvt-secundario",
    explodedViewId: "cvt-secundaria-embreagem-explodida.svg",
    calloutBalloonNumber: 5,
    description:
      "Mola helicoidal de contraste com pré-carga de 35 a 50 kgf para retorno das polias em aclives.",
    nominalDimensionMm: 139.5,
    serviceLimitMm: 134.0,
    torqueSpec: "Sob pré-carga na prensa",
    requiredTool: "Paquímetro de 150/200 mm + Compressor de mola 07LME-GZ40201",
    criticalGotcha:
      "Comprimento livre mínimo: 134,0 mm. Mola fadigada causa perda de força (kick-down lento) em subidas.",
    isWearItem: true,
  },
  {
    id: "oem-porca-mola-90202-187",
    partNumber: "90202-187-000",
    name: "Porca Central Plana M28 da Sapata de Embreagem (39 mm)",
    nameEn: "Special Nut M28 x 1.0 (Thin 39mm)",
    subsystem: "cvt",
    categoryLabel: "Embreagem Secundária",
    hotspotId: "hotspot-cvt-secundario",
    explodedViewId: "cvt-secundaria-embreagem-explodida.svg",
    calloutBalloonNumber: 4,
    description:
      "Porca especial de baixo perfil (4,5 mm de espessura) com sextavado de 39 mm e rosca M28 x 1,0.",
    torqueSpec: "54 N.m (5,5 kgf.m)",
    requiredTool: "Soquete plano sem chanfro 39 mm 07GMA-KS40100 + Prensa 07LME-GZ40201",
    criticalGotcha:
      "PERIGO BALÍSTICO: Desmontar e montar EXCLUSIVAMENTE no compressor de mola. A ejeção sem contenção causa ferimentos graves.",
    isWearItem: false,
  },
  {
    id: "oem-pinos-torque-cam-23225",
    partNumber: "23225-KS4-000",
    name: "Pinos Guia da Canaleta de Torque Cam (3 un)",
    nameEn: "Torque Cam Guide Pin (3 pcs)",
    subsystem: "cvt",
    categoryLabel: "Polia Secundária",
    hotspotId: "hotspot-cvt-secundario",
    explodedViewId: "cvt-secundaria-embreagem-explodida.svg",
    calloutBalloonNumber: 6,
    description:
      "Pinos cilíndricos de aço retificado com roletes tubulares que deslizam nas canaletas helicoidais.",
    torqueSpec: "Lubrificação graxa poliureia",
    requiredTool: "Graxa de poliureia MoS2 para CVT",
    criticalGotcha:
      "Lubrificar exclusivamente com graxa de poliureia de alta temperatura com MoS2. Graxa comum vaza nas sapatas.",
    isWearItem: true,
  },
  {
    id: "oem-bujao-redutor-90131",
    partNumber: "90131-896-650",
    name: "Bujões de Nível e Dreno da Transmissão Final M8 (2 un)",
    nameEn: "Drain & Check Bolt M8",
    subsystem: "cvt",
    categoryLabel: "Redutor Final",
    hotspotId: "hotspot-redutor-final",
    explodedViewId: "redutor-oleo-bujoes.svg",
    calloutBalloonNumber: 1,
    description:
      "Parafusos sextavados de 12 mm com rosca métrica M8 x 1,25 para drenagem e checagem de nível.",
    torqueSpec: "13 N.m (1,3 kgf.m)",
    requiredTool: "Torquímetro de escala baixa + Soquete 12 mm",
    criticalGotcha:
      "Aperto além de 13 N.m arromba a carcaça de alumínio da balança. Capacidade: 120 ml Pro Honda 10W-30 JASO MB.",
    isWearItem: false,
  },
  {
    id: "oem-arruela-redutor-90495",
    partNumber: "90495-MN5-000",
    name: "Arruelas de Vedação de Alumínio 8 mm do Redutor (2 un)",
    nameEn: "Sealing Washer 8mm (2 pcs)",
    subsystem: "cvt",
    categoryLabel: "Redutor Final",
    hotspotId: "hotspot-redutor-final",
    explodedViewId: "redutor-oleo-bujoes.svg",
    calloutBalloonNumber: 2,
    description:
      "Arruelas de alumínio de esmagamento para vedação estanque dos bujões da transmissão final.",
    torqueSpec: "Sob os bujões M8 a 13 N.m",
    requiredTool: "Troca compulsória de uso único",
    criticalGotcha:
      "Substituir ambas as arruelas a cada troca de óleo para evitar vazamento na roda traseira.",
    isWearItem: true,
  },

  // ==========================================
  // GRUPO 2: MOTOR eSP 149,3 cm³
  // ==========================================
  {
    id: "oem-bujao-motor-90131",
    partNumber: "90131-883-000",
    name: "Bujão de Dreno do Óleo do Cárter M12 (Sext. 12 mm)",
    nameEn: "Oil Drain Plug M12 x 1.5",
    subsystem: "motor",
    categoryLabel: "Lubrificação eSP",
    hotspotId: "hotspot-motor-carter",
    explodedViewId: "motor-tela-oleo-bujoes.svg",
    calloutBalloonNumber: 1,
    description:
      "Parafuso flangeado M12 x 1,5 na base inferior esquerda do cárter para esgotamento do óleo 10W-30.",
    torqueSpec: "24 N.m (2,4 kgf.m)",
    requiredTool: "Torquímetro + Soquete 12 mm",
    criticalGotcha:
      "Substituir sempre a arruela de esmagamento de 12 mm. Capacidade de abastecimento: 800 ml no dreno.",
    isWearItem: false,
  },
  {
    id: "oem-arruela-motor-94109",
    partNumber: "94109-12000",
    name: "Arruela de Esmagamento de Alumínio 12 mm do Cárter",
    nameEn: "Oil Drain Washer 12mm",
    subsystem: "motor",
    categoryLabel: "Lubrificação eSP",
    hotspotId: "hotspot-motor-carter",
    explodedViewId: "motor-tela-oleo-bujoes.svg",
    calloutBalloonNumber: 2,
    description:
      "Arruela deformável em liga pura de alumínio para vedação estanque contra a galeria do cárter.",
    torqueSpec: "Sob o bujão M12 a 24 N.m",
    requiredTool: "Uso único (descarte obrigatório)",
    criticalGotcha:
      "Reutilizar a arruela velha gera gotejamento crônico e induz mecânico desavisado a apertar além de 24 N.m.",
    isWearItem: true,
  },
  {
    id: "oem-tela-oleo-15421",
    partNumber: "15421-KPL-900",
    name: "Tela / Peneira Filtrante de Óleo Cilíndrica em Inox",
    nameEn: "Oil Strainer Screen",
    subsystem: "motor",
    categoryLabel: "Lubrificação eSP",
    hotspotId: "hotspot-motor-carter",
    explodedViewId: "motor-tela-oleo-bujoes.svg",
    calloutBalloonNumber: 3,
    description:
      "Elemento filtrante primário em malha de aço inox para retenção de limalhas na sucção da bomba.",
    torqueSpec: "Limpeza a cada 12.000 km",
    requiredTool: "Desengraxante a seco / ar suave",
    criticalGotcha: "Montar com o lábio de borracha voltado para dentro do bloco do motor.",
    isWearItem: false,
  },
  {
    id: "oem-mola-tela-15426",
    partNumber: "15426-KVB-900",
    name: "Mola Cônica de Retenção da Tela de Óleo",
    nameEn: "Oil Strainer Spring",
    subsystem: "motor",
    categoryLabel: "Lubrificação eSP",
    hotspotId: "hotspot-motor-carter",
    explodedViewId: "motor-tela-oleo-bujoes.svg",
    calloutBalloonNumber: 4,
    description:
      "Mola cônica de sustentação axial que pressiona a tela contra a galeria interna do cárter.",
    torqueSpec: "Assenta na tampa M30",
    requiredTool: "Inspeção visual de posicionamento",
    criticalGotcha: "Montar com a base mais larga apoiada no interior da tampa sextavada de 17 mm.",
    isWearItem: false,
  },
  {
    id: "oem-tampa-tela-12361",
    partNumber: "12361-035-000",
    name: "Tampa Sextavada 17 mm da Tela de Óleo (Rosca M30)",
    nameEn: "Oil Strainer Cap M30 x 1.5",
    subsystem: "motor",
    categoryLabel: "Lubrificação eSP",
    hotspotId: "hotspot-motor-carter",
    explodedViewId: "motor-tela-oleo-bujoes.svg",
    calloutBalloonNumber: 5,
    description:
      "Tampa de alumínio fundido com rosca métrica larga M30 x 1,5 na lateral esquerda inferior.",
    torqueSpec: "20 N.m (2,0 kgf.m)",
    requiredTool: "Torquímetro + Soquete 17 mm",
    criticalGotcha:
      "Rosquear totalmente à mão para não cruzar os fios no alumínio. Lubrificar O-ring com óleo novo.",
    isWearItem: false,
  },
  {
    id: "oem-oring-tela-91302",
    partNumber: "91302-001-020",
    name: "Anel O-Ring 30,8 x 3 mm da Tampa da Tela de Óleo",
    nameEn: "O-Ring 30.8 x 3mm",
    subsystem: "motor",
    categoryLabel: "Lubrificação eSP",
    hotspotId: "hotspot-motor-carter",
    explodedViewId: "motor-tela-oleo-bujoes.svg",
    calloutBalloonNumber: 6,
    description:
      "Anel de borracha elastomérica nitrílica para vedação térmica e hidráulica da tampa M30.",
    torqueSpec: "Instalação lubrificada",
    requiredTool: "Substituir a cada 2 revisões",
    criticalGotcha:
      "Nunca montar a seco; a torção do anel durante o aperto causa corte e vazamento.",
    isWearItem: true,
  },
  {
    id: "oem-tampa-cabecote-12310",
    partNumber: "12310-K0W-N00",
    name: "Tampa do Cabeçote de Alumínio",
    nameEn: "Cylinder Head Cover",
    subsystem: "motor",
    categoryLabel: "Cabeçote e Válvulas",
    hotspotId: "hotspot-motor-cabecote",
    explodedViewId: "motor-sincronismo-pms-valvulas.svg",
    calloutBalloonNumber: 1,
    description:
      "Tampa superior do comando de válvulas e balancins roletados com coxins antivibração.",
    torqueSpec: "10 N.m (1,0 kgf.m)",
    requiredTool: "Soquete 10 mm + Torquímetro",
    criticalGotcha:
      "2 parafusos flangeados M6; aperto uniforme progressivo para assentamento da junta perimetral.",
    isWearItem: false,
  },
  {
    id: "oem-junta-cabecote-12391",
    partNumber: "12391-K97-T00",
    name: "Junta de Borracha Nitrílica da Tampa do Cabeçote",
    nameEn: "Head Cover Packing",
    subsystem: "motor",
    categoryLabel: "Cabeçote e Válvulas",
    hotspotId: "hotspot-motor-cabecote",
    explodedViewId: "motor-sincronismo-pms-valvulas.svg",
    calloutBalloonNumber: 2,
    description:
      "Guarnição de borracha vulcanizada com perfil exato de assentamento nas meias-luas do cabeçote.",
    torqueSpec: "Sob a tampa a 10 N.m",
    requiredTool: "Inspeção de trincas / ressecamento",
    criticalGotcha:
      "Inspecionar os cantos das meias-luas; aplicar ponto minúsculo de junta líquida se previsto pelo manual.",
    isWearItem: true,
  },
  {
    id: "oem-valvula-adm-14711",
    partNumber: "14711-K97-T00",
    name: "Válvula de Admissão OHC (Folga a Frio < 35°C)",
    nameEn: "Intake Valve (IN)",
    subsystem: "motor",
    categoryLabel: "Cabeçote e Válvulas",
    hotspotId: "hotspot-motor-cabecote",
    explodedViewId: "motor-sincronismo-pms-valvulas.svg",
    calloutBalloonNumber: 3,
    description: "Válvula de admissão acionada por balancim roletado no cabeçote eSP.",
    nominalDimensionMm: 0.1,
    serviceLimitMm: 0.12,
    torqueSpec: "Contraporca: 10 N.m",
    requiredTool: "Cálibre de lâminas centesimal (lâmina 0,10 mm justa; 0,13 mm não entra)",
    criticalGotcha:
      "REGULAGEM EXCLUSIVAMENTE A FRIO (< 35°C com motor parado há mais de 6h). Válvula quente enforca a admissão.",
    isWearItem: false,
  },
  {
    id: "oem-valvula-esc-14721",
    partNumber: "14721-K97-T00",
    name: "Válvula de Escape OHC (Folga a Frio < 35°C)",
    nameEn: "Exhaust Valve (EX)",
    subsystem: "motor",
    categoryLabel: "Cabeçote e Válvulas",
    hotspotId: "hotspot-motor-cabecote",
    explodedViewId: "motor-sincronismo-pms-valvulas.svg",
    calloutBalloonNumber: 4,
    description:
      "Válvula de escape sujeita a altas cargas térmicas acionada por balancim roletado.",
    nominalDimensionMm: 0.24,
    serviceLimitMm: 0.26,
    torqueSpec: "Contraporca: 10 N.m",
    requiredTool: "Cálibre de lâminas centesimal (lâmina 0,24 mm justa; 0,28 mm não entra)",
    criticalGotcha:
      "Manter o parafuso quadrado de regulagem imobilizado ao apertar a contraporca de 9 mm para não alterar a folga.",
    isWearItem: false,
  },
  {
    id: "oem-contraporca-valvula-90206",
    partNumber: "90206-001-000",
    name: "Contraporcas M5 de Ajuste de Folga de Válvula (2 un)",
    nameEn: "Tappet Adjusting Nut M5",
    subsystem: "motor",
    categoryLabel: "Cabeçote e Válvulas",
    hotspotId: "hotspot-motor-cabecote",
    explodedViewId: "motor-sincronismo-pms-valvulas.svg",
    calloutBalloonNumber: 5,
    description:
      "Porcas sextavadas de 9 mm com rosca fina M5 para travamento dos parafusos de balancim.",
    torqueSpec: "10 N.m (1,0 kgf.m)",
    requiredTool: "Chave de estria 9 mm + chave quadrada de tucho",
    criticalGotcha:
      "Aperto além de 10 N.m espanar a rosca M5 do balancim, exigindo troca do balancim completo.",
    isWearItem: false,
  },
  {
    id: "oem-vela-ignicao-31918",
    partNumber: "31918-K97-T01",
    name: "Vela de Ignição Especial NGK MR8K-9 (Rosca M10)",
    nameEn: "Spark Plug NGK MR8K-9",
    subsystem: "motor",
    categoryLabel: "Ignição eSP",
    hotspotId: "hotspot-motor-cabecote",
    explodedViewId: "motor-vela-ngk-mr8k9-31918-k97-t01.svg",
    calloutBalloonNumber: 1,
    description:
      "Vela especial de grau térmico 8 frio com rosca métrica longa M10 x 1,0 e gap calibrado de 0,80 a 0,90 mm.",
    nominalDimensionMm: 0.85,
    serviceLimitMm: 0.9,
    torqueSpec: "16 N.m (1,6 kgf.m)",
    requiredTool: "Chave de vela articulada 16 mm com retentor de borracha",
    criticalGotcha:
      "Rosquear totalmente à mão por 4 a 5 voltas. Proibido usar chave desalinhada para não partir a cerâmica.",
    isWearItem: true,
  },
  {
    id: "oem-sensor-eot-37870",
    partNumber: "37870-KZR-601",
    name: "Sensor de Temperatura do Óleo do Motor (EOT)",
    nameEn: "Engine Oil Temperature Sensor (EOT)",
    subsystem: "motor",
    categoryLabel: "Sensores eSP",
    hotspotId: "hotspot-motor-cabecote",
    explodedViewId: "motor-sincronismo-pms-valvulas.svg",
    calloutBalloonNumber: 6,
    description:
      "Termistor NTC imerso na galeria de óleo do cabeçote (resistência 2,5 a 2,8 kΩ a 20°C).",
    torqueSpec: "15 N.m (1,5 kgf.m)",
    requiredTool: "Chave profunda 17 mm",
    criticalGotcha:
      "Ponto-chave da rotina de calibração do sensor TP após intervenção no motor/cabeçote.",
    isWearItem: false,
  },
  {
    id: "oem-radiador-19010",
    partNumber: "19010-K0W-N01",
    name: "Radiador Lateral de Alumínio",
    nameEn: "Radiator Comp.",
    subsystem: "motor",
    categoryLabel: "Arrefecimento Líquido",
    hotspotId: "hotspot-motor-radiador",
    explodedViewId: "arrefecimento-radiador-tampa-108kpa.svg",
    calloutBalloonNumber: 1,
    description:
      "Radiador lateral compacto fundido em alumínio acoplado ao fluxo de ar da ventoinha do virabrequim.",
    torqueSpec: "9 N.m (0,9 kgf.m)",
    requiredTool: "Inspeção de desobstrução das aletas",
    criticalGotcha:
      "Lavar somente com jato de baixa pressão em linha reta. Nunca desentortar aletas com fenda pontiaguda.",
    isWearItem: false,
  },
  {
    id: "oem-tampa-rad-19037",
    partNumber: "19037-K0W-N01",
    name: "Tampa Pressurizada do Radiador (108 kPa / 1,1 kgf/cm²)",
    nameEn: "Radiator Cap (108 kPa)",
    subsystem: "motor",
    categoryLabel: "Arrefecimento Líquido",
    hotspotId: "hotspot-motor-radiador",
    explodedViewId: "arrefecimento-radiador-tampa-108kpa.svg",
    calloutBalloonNumber: 2,
    description:
      "Tampa metálica de dupla válvula calibrada a 108 kPa para elevação do ponto de ebulição do fluido a 130°C.",
    nominalDimensionMm: 108.0,
    serviceLimitMm: 93.0,
    torqueSpec: "Encaixe baioneta manual",
    requiredTool: "Bomba de teste de tampa de radiador",
    criticalGotcha: "Substituir se abrir com pressão inferior a 93 kPa ou travar acima de 123 kPa.",
    isWearItem: true,
  },
  {
    id: "oem-bomba-agua-19200",
    partNumber: "19200-K97-T00",
    name: "Conjunto da Bomba d'Água Centrífuga eSP",
    nameEn: "Water Pump Comp.",
    subsystem: "motor",
    categoryLabel: "Arrefecimento Líquido",
    hotspotId: "hotspot-motor-radiador",
    explodedViewId: "arrefecimento-radiador-tampa-108kpa.svg",
    calloutBalloonNumber: 3,
    description:
      "Bomba de circulação forçada com selo mecânico cerâmico acionada diretamente pelo virabrequim.",
    torqueSpec: "10 N.m (1,0 kgf.m)",
    requiredTool: "Inspeção do orifício weep-hole",
    criticalGotcha:
      "Uso de aditivos baratos com silicato destrói o selo cerâmico, contaminando o cárter com coolant.",
    isWearItem: false,
  },
  {
    id: "oem-dreno-coolant-90004",
    partNumber: "90004-GHB-600",
    name: "Parafuso de Dreno da Bomba d'Água M6",
    nameEn: "Coolant Drain Bolt M6",
    subsystem: "motor",
    categoryLabel: "Arrefecimento Líquido",
    hotspotId: "hotspot-motor-radiador",
    explodedViewId: "arrefecimento-radiador-tampa-108kpa.svg",
    calloutBalloonNumber: 4,
    description:
      "Parafuso flangeado sextavado de 8 mm na face inferior da carcaça da bomba d'água.",
    torqueSpec: "10 N.m (1,0 kgf.m)",
    requiredTool: "Soquete 8 mm + Torquímetro",
    criticalGotcha: "Substituir sempre a arruela de vedação de cobre de 6,5 mm a cada desmontagem.",
    isWearItem: false,
  },
  {
    id: "oem-arruela-dreno-coolant-90441",
    partNumber: "90441-ME9-000",
    name: "Arruela de Vedação de Cobre 6,5 mm da Bomba d'Água",
    nameEn: "Copper Sealing Washer 6.5mm",
    subsystem: "motor",
    categoryLabel: "Arrefecimento Líquido",
    hotspotId: "hotspot-motor-radiador",
    explodedViewId: "arrefecimento-radiador-tampa-108kpa.svg",
    calloutBalloonNumber: 5,
    description:
      "Arruela de cobre recozido macio de uso único para assentamento estanque do dreno do líquido.",
    torqueSpec: "Sob o dreno M6 a 10 N.m",
    requiredTool: "Substituição obrigatória",
    criticalGotcha:
      "Arruela amassada reutilizada causa vazamento lento sob pressão a 108 kPa em viagens longas.",
    isWearItem: true,
  },
  {
    id: "oem-capa-defletora-19150",
    partNumber: "19150-K0W-N00",
    name: "Capa Defletora Externa do Radiador Lateral",
    nameEn: "Radiator Shroud",
    subsystem: "motor",
    categoryLabel: "Arrefecimento Líquido",
    hotspotId: "hotspot-motor-radiador",
    explodedViewId: "arrefecimento-radiador-tampa-108kpa.svg",
    calloutBalloonNumber: 6,
    description:
      "Duto plástico envolvente que canaliza o fluxo aerodinâmico soprado pela hélice centrífuga.",
    torqueSpec: "9,0 N.m (0,9 kgf.m)",
    requiredTool: "Chave Phillips JIS #2 / Soquete 8 mm",
    criticalGotcha:
      "Garantir encaixe correto das travas para evitar ressonância e ruído em rotações médias.",
    isWearItem: false,
  },

  // ==========================================
  // GRUPO 3: INJEÇÃO ELETRÔNICA PGM-FI
  // ==========================================
  {
    id: "oem-corpo-tbi-16400",
    partNumber: "16400-K0W-N01",
    name: "Corpo de Borboleta TBI Keihin 26 mm com Película MoS2",
    nameEn: "Throttle Body Ass'y 26mm",
    subsystem: "pgmfi",
    categoryLabel: "Alimentação PGM-FI",
    hotspotId: "hotspot-pgmfi-tbi",
    explodedViewId: "pgmfi-tbi-keihin-mos2.svg",
    calloutBalloonNumber: 1,
    description:
      "Corpo de aceleração usinado em alumínio com borboleta de 26,0 mm dotada de película selante de bissulfeto de molibdênio.",
    torqueSpec: "Coletor: 10 N.m",
    requiredTool: "Álcool isopropílico 99% + Pincel macio",
    criticalGotcha:
      "PROIBIDO RASPAGEM COM PALHA DE AÇO OU SOLVENTES CLORADOS (CAR 80). Remover a película de MoS2 inutiliza o corpo e desregula a marcha lenta.",
    isWearItem: false,
  },
  {
    id: "oem-injetor-16450",
    partNumber: "16450-K0W-N01",
    name: "Eletroinjetor de Combustível PGM-FI (6 Furos)",
    nameEn: "Fuel Injector Ass'y (6 Holes)",
    subsystem: "pgmfi",
    categoryLabel: "Alimentação PGM-FI",
    hotspotId: "hotspot-pgmfi-tbi",
    explodedViewId: "pgmfi-tbi-keihin-mos2.svg",
    calloutBalloonNumber: 2,
    description:
      "Bico injetor de alta precisão com microatomização em leque (resistência ôhmica 11 a 13 Ω; pressão de linha 294 kPa).",
    nominalDimensionMm: 12.0,
    serviceLimitMm: 13.0,
    torqueSpec: "10 N.m (1,0 kgf.m)",
    requiredTool: "Multímetro digital calibrado",
    criticalGotcha:
      "Lubrificar anel O-ring do injetor exclusivamente com óleo de motor novo; nunca usar vaselina ou graxa.",
    isWearItem: false,
  },
  {
    id: "oem-valvula-iacv-16430",
    partNumber: "16430-GFM-902",
    name: "Válvula de Controle de Marcha Lenta (IACV)",
    nameEn: "Idle Air Control Valve (IACV)",
    subsystem: "pgmfi",
    categoryLabel: "Alimentação PGM-FI",
    hotspotId: "hotspot-pgmfi-tbi",
    explodedViewId: "pgmfi-tbi-keihin-mos2.svg",
    calloutBalloonNumber: 3,
    description:
      "Motor de passo digital acoplado ao duto de ar de bypass para estabilização de rotação em 1.700 ± 100 rpm.",
    torqueSpec: "2,1 N.m (0,21 kgf.m)",
    requiredTool: "Chave Torx / JIS #2",
    criticalGotcha:
      "Limpar haste da agulha com delicadeza; lubrificar O-ring com graxa pura de silicone.",
    isWearItem: false,
  },
  {
    id: "oem-sensor-unit-16060",
    partNumber: "16060-KVS-J01",
    name: "Unidade de Sensores Integrada 3-em-1 (TP / MAP / IAT)",
    nameEn: "Sensor Unit Comp. (TP/MAP/IAT)",
    subsystem: "pgmfi",
    categoryLabel: "Sensores PGM-FI",
    hotspotId: "hotspot-pgmfi-tbi",
    explodedViewId: "pgmfi-tbi-keihin-mos2.svg",
    calloutBalloonNumber: 4,
    description:
      "Módulo integrado contendo sensor de posição de borboleta (TP), sensor de pressão absoluta (MAP) e temperatura do ar (IAT).",
    torqueSpec: "Fixação calibrada de fábrica",
    requiredTool: "Multímetro / Scanner Honda",
    criticalGotcha:
      "Sinal do sensor TP com borboleta fechada: 0,4 a 0,6 V; aberta: 4,2 a 4,8 V. Exige rotina de reset após desmontagem.",
    isWearItem: false,
  },
  {
    id: "oem-sonda-lambda-36532",
    partNumber: "36532-K0W-N01",
    name: "Sensor de Oxigênio Aquecido no Escapamento (Sonda Lambda M12)",
    nameEn: "Oxygen Sensor M12 (O2)",
    subsystem: "pgmfi",
    categoryLabel: "Sensores PGM-FI",
    hotspotId: "hotspot-pgmfi-tbi",
    explodedViewId: "pgmfi-tbi-keihin-mos2.svg",
    calloutBalloonNumber: 5,
    description:
      "Sensor eletroquímico de dióxido de zircônio aquecido rosqueado na curva de escape para malha fechada estequiométrica.",
    torqueSpec: "25 N.m (2,5 kgf.m)",
    requiredTool: "Chave de fenda de sonda 17 mm aberta",
    criticalGotcha:
      "Desrosquear exclusivamente com escapamento 100% frio para não arrancar os fios de rosca do tubo.",
    isWearItem: false,
  },
  {
    id: "oem-jumper-scs-070pz",
    partNumber: "070PZ-ZY30100",
    name: "Conector Jumper de Serviço SCS para Tomada DLC",
    nameEn: "SCS Service Connector",
    subsystem: "pgmfi",
    categoryLabel: "Ferramental / Diagnóstico",
    hotspotId: "hotspot-eletrica-dlc",
    explodedViewId: "pgmfi-dlc-jumper-scs.svg",
    calloutBalloonNumber: 1,
    description:
      "Chave jumper de serviço com terminais de latão para curto-circuito seguro dos pinos SCS na tomada vermelha.",
    torqueSpec: "Encaixe rápido mecânico",
    requiredTool: "Conector SCS genuíno Honda",
    criticalGotcha:
      "ALERTA MÁXIMO: Interligar exclusivamente o fio Marrom (SCS) e Verde/Preto (Terra). TOCAR NO PINO VERMELHO/AMARELO (+12V) QUEIMA O ECM.",
    isWearItem: false,
  },
  {
    id: "oem-ecm-38770",
    partNumber: "38770-K0W-N01",
    name: "Módulo de Controle Eletrônico do Motor (ECM 32 bits)",
    nameEn: "Engine Control Module (ECM)",
    subsystem: "pgmfi",
    categoryLabel: "Gerenciamento Eletrônico",
    hotspotId: "hotspot-eletrica-dlc",
    explodedViewId: "pgmfi-dlc-jumper-scs.svg",
    calloutBalloonNumber: 2,
    description:
      "Computador de bordo dedicado que processa injeção, avanço de ignição, partida silenciosa ACG e Idling Stop.",
    torqueSpec: "Coxim elástico no chassi",
    requiredTool: "Preservação estática",
    criticalGotcha:
      "Desconectar bateria antes de desconectar os chicotes do ECM. Código MIL 33 indica falha interna na EEPROM.",
    isWearItem: false,
  },

  // ==========================================
  // GRUPO 4: FREIOS NISSIN & SISTEMA ABS
  // ==========================================
  {
    id: "oem-disco-diant-45251",
    partNumber: "45251-K0W-N01",
    name: "Disco de Freio Dianteiro Wave 240 mm Nissin",
    nameEn: "Front Brake Disc Wave 240mm",
    subsystem: "freios",
    categoryLabel: "Freio Dianteiro",
    hotspotId: "hotspot-freio-diant",
    explodedViewId: "freio-disco-wave-tolerancia.svg",
    calloutBalloonNumber: 1,
    description:
      "Disco perfurado tipo Wave em aço inoxidável cirúrgico com diâmetro externo de 240 mm e 4 furos de fixação M8.",
    nominalDimensionMm: 4.0,
    serviceLimitMm: 3.5,
    torqueSpec: "42 N.m (4,3 kgf.m)",
    requiredTool: "Micrômetro externo de ponta plana (DIN 863) + Torquímetro",
    criticalGotcha:
      "DESCARTE MANDATÓRIO: Gravado no disco MIN. TH. 3.5 mm. Se espessura ≤ 3,50 mm, substituir imediatamente por segurança.",
    isWearItem: true,
  },
  {
    id: "oem-disco-tras-43251",
    partNumber: "43251-K0W-N01",
    name: "Disco de Freio Traseiro Wave 220 mm Nissin",
    nameEn: "Rear Brake Disc Wave 220mm",
    subsystem: "freios",
    categoryLabel: "Freio Traseiro",
    hotspotId: "hotspot-freio-tras",
    explodedViewId: "freio-disco-wave-tolerancia.svg",
    calloutBalloonNumber: 2,
    description:
      "Disco perfurado tipo Wave em aço inoxidável com diâmetro de 220 mm para desaceleração traseira precisa.",
    nominalDimensionMm: 4.0,
    serviceLimitMm: 3.5,
    torqueSpec: "42 N.m (4,3 kgf.m)",
    requiredTool: "Micrômetro centesimal",
    criticalGotcha:
      "Aplicar trava química média azul nos 4 parafusos de retenção do cubo traseiro a 42 N.m.",
    isWearItem: true,
  },
  {
    id: "oem-caliper-diant-45150",
    partNumber: "45150-K0W-N01",
    name: "Cáliper Dianteiro Nissin de Duplo Pistão",
    nameEn: "Front Caliper Ass'y (2-Piston Nissin)",
    subsystem: "freios",
    categoryLabel: "Freio Dianteiro",
    hotspotId: "hotspot-freio-diant",
    explodedViewId: "freio-pastilhas-nissin-diant-tras.svg",
    calloutBalloonNumber: 1,
    description:
      "Pinça hidráulica dianteira flutuante com duplo êmbolo de 25,4 mm ancorada na bengala Showa esquerda.",
    torqueSpec: "30 N.m (3,1 kgf.m)",
    requiredTool: "Soquete 12 mm + Torquímetro",
    criticalGotcha:
      "Limpar poeira de freio nos pistões com álcool isopropílico antes de recuá-los para troca de pastilhas.",
    isWearItem: false,
  },
  {
    id: "oem-caliper-tras-43150",
    partNumber: "43150-K0W-N01",
    name: "Cáliper Traseiro Nissin de Pistão Simples",
    nameEn: "Rear Caliper Ass'y (1-Piston Nissin)",
    subsystem: "freios",
    categoryLabel: "Freio Traseiro",
    hotspotId: "hotspot-freio-tras",
    explodedViewId: "freio-pastilhas-nissin-diant-tras.svg",
    calloutBalloonNumber: 2,
    description:
      "Pinça flutuante de pistão simples montada sobre suporte usinado na balança direita de alumínio.",
    torqueSpec: "22 N.m (2,2 kgf.m)",
    requiredTool: "Soquete 10/12 mm + Torquímetro",
    criticalGotcha:
      "Aplicar graxa Shin-Etsu de silicone puro nos pinos deslizantes para preservar as buchas de EPDM.",
    isWearItem: false,
  },
  {
    id: "oem-pastilhas-diant-06455",
    partNumber: "06455-K0W-N01",
    name: "Jogo de Pastilhas de Freio Dianteiro Nissin Genuínas",
    nameEn: "Front Brake Pad Set Nissin",
    subsystem: "freios",
    categoryLabel: "Pastilhas de Freio",
    hotspotId: "hotspot-freio-diant",
    explodedViewId: "freio-pastilhas-nissin-diant-tras.svg",
    calloutBalloonNumber: 3,
    description:
      "Pastilhas sinterizadas de alta fricção com placas antirruído em inox e sulco indicador de desgaste.",
    nominalDimensionMm: 4.5,
    serviceLimitMm: 1.0,
    torqueSpec: "Pino guia: 17 N.m",
    requiredTool: "Paquímetro ou inspeção visual da ranhura indicadora",
    criticalGotcha:
      "Limite de descarte compulsório em 1,0 mm. Se o fundo da ranhura sumir, trocar imediatamente.",
    isWearItem: true,
  },
  {
    id: "oem-pastilhas-tras-06435",
    partNumber: "06435-K0W-N01",
    name: "Jogo de Pastilhas de Freio Traseiro Nissin Genuínas",
    nameEn: "Rear Brake Pad Set Nissin",
    subsystem: "freios",
    categoryLabel: "Pastilhas de Freio",
    hotspotId: "hotspot-freio-tras",
    explodedViewId: "freio-pastilhas-nissin-diant-tras.svg",
    calloutBalloonNumber: 4,
    description:
      "Pastilhas sinterizadas genuínas Nissin para disco Wave traseiro de 220 mm com placas calço térmico.",
    nominalDimensionMm: 4.5,
    serviceLimitMm: 1.0,
    torqueSpec: "Pino guia: 17 N.m",
    requiredTool: "Paquímetro centesimal",
    criticalGotcha: "Substituir sempre o jogo completo de pastilhas. Nunca trocar somente um lado.",
    isWearItem: true,
  },
  {
    id: "oem-sensor-abs-38520",
    partNumber: "38520-K0W-N01",
    name: "Sensor de Velocidade de Roda ABS Dianteiro (Efeito Hall)",
    nameEn: "Front Wheel Speed Sensor (ABS)",
    subsystem: "freios",
    categoryLabel: "Sistema ABS",
    hotspotId: "hotspot-freio-diant",
    explodedViewId: "freio-abs-circuito-blindagem.svg",
    calloutBalloonNumber: 1,
    description:
      "Sensor magnético de efeito Hall montado na ponta inferior da bengala esquerda em frente à roda fônica.",
    nominalDimensionMm: 0.8,
    serviceLimitMm: 1.2,
    torqueSpec: "10 N.m (1,0 kgf.m)",
    requiredTool: "Cálibre de lâminas (folga de 0,40 a 1,20 mm)",
    criticalGotcha:
      "Preservar distância de trabalho sem encostar no disco perfurado. Limpar limalhas magnéticas com pincel.",
    isWearItem: false,
  },
  {
    id: "oem-fônica-abs-44515",
    partNumber: "44515-K0W-N00",
    name: "Anel Pulsador Fônico do ABS (Roda Fônica 30 Furos)",
    nameEn: "ABS Pulser Ring (30 Slots)",
    subsystem: "freios",
    categoryLabel: "Sistema ABS",
    hotspotId: "hotspot-freio-diant",
    explodedViewId: "freio-abs-circuito-blindagem.svg",
    calloutBalloonNumber: 2,
    description:
      "Disco com 30 janelas retangulares usinadas fixado no cubo dianteiro para geração de trem de pulsos.",
    torqueSpec: "Parafusos Torx: 7 N.m",
    requiredTool: "Inspeção de empenamento",
    criticalGotcha:
      "Dentes amassados por montagem com espátula geram código de erro do ABS e pulsação fantasma na manete.",
    isWearItem: false,
  },
  {
    id: "oem-modulador-abs-57110",
    partNumber: "57110-K0W-N01",
    name: "Modulador Eletro-Hidráulico do ABS Monocanal Nissin",
    nameEn: "ABS Modulator Unit Comp.",
    subsystem: "freios",
    categoryLabel: "Sistema ABS",
    hotspotId: "hotspot-freio-diant",
    explodedViewId: "freio-abs-circuito-blindagem.svg",
    calloutBalloonNumber: 3,
    description:
      "Bloco hidráulico de alta precisão com micro-válvulas solenoides e bomba de retorno montado sob o escudo frontal.",
    torqueSpec: "Banjos M10: 34 N.m",
    requiredTool: "Sangria pressurizada contínua",
    criticalGotcha:
      "REGRA DE OURO (REGRA DOS 30%): O reservatório do guidão NUNCA pode esvaziar durante a sangria. Se entrar ar no bloco, a sangria manual não purga.",
    isWearItem: false,
  },

  // ==========================================
  // GRUPO 5: CICLÍSTICA & SUSPENSÃO SHOWA
  // ==========================================
  {
    id: "oem-garfo-diant-51400",
    partNumber: "51400-K0W-N11",
    name: "Conjunto do Garfo Dianteiro Telescópico Showa 31 mm",
    nameEn: "Front Fork Ass'y Showa 31mm (130mm Travel)",
    subsystem: "suspensao",
    categoryLabel: "Suspensão Dianteira",
    hotspotId: "hotspot-susp-diant",
    explodedViewId: "suspensao-garfo-showa-31mm.svg",
    calloutBalloonNumber: 1,
    description:
      "Bengala dianteira Showa com tubos cromados de 31 mm e curso líder de 130 mm com mola progressiva.",
    torqueSpec: "Mesa inferior M10: 27 N.m",
    requiredTool: "Chave sextavada 14 mm + Torquímetro",
    criticalGotcha:
      "Aperto cruzado alternado balanceado em 2 passos para não empenar os braços de alumínio da mesa.",
    isWearItem: false,
  },
  {
    id: "oem-retentor-garfo-51490",
    partNumber: "51490-K0W-N11",
    name: "Jogo de Retentores Hidráulicos e Guarda-Pós do Garfo Showa",
    nameEn: "Front Fork Seal Set Showa",
    subsystem: "suspensao",
    categoryLabel: "Suspensão Dianteira",
    hotspotId: "hotspot-susp-diant",
    explodedViewId: "suspensao-garfo-showa-31mm.svg",
    calloutBalloonNumber: 2,
    description:
      "Kit com retentor de óleo de duplo lábio com mola inox e anel guarda-pó de proteção externa.",
    torqueSpec: "Prensa com batedor de retentor 31 mm",
    requiredTool: "Instalador de retentor Showa 31 mm",
    criticalGotcha:
      "Trocar sempre em pares. Cobrir a ponta do tubo com fita adesiva para não cortar o lábio ao montar.",
    isWearItem: true,
  },
  {
    id: "oem-oleo-garfo-08208",
    partNumber: "08208-0010",
    name: "Fluido Hidráulico de Bengala Honda Fork Oil 10W",
    nameEn: "Honda Fork Oil 10W",
    subsystem: "suspensao",
    categoryLabel: "Suspensão Dianteira",
    hotspotId: "hotspot-susp-diant",
    explodedViewId: "suspensao-garfo-showa-31mm.svg",
    calloutBalloonNumber: 3,
    description:
      "Óleo hidráulico com aditivos antiespuma de alta estabilidade térmica e viscosidade SAE 10W.",
    nominalDimensionMm: 115.0,
    serviceLimitMm: 115.0,
    torqueSpec: "Capacidade: ~115 ml por bengala",
    requiredTool: "Proveta graduada / régua de medição de nível de ar",
    criticalGotcha:
      "Aferir coluna de ar superior sem mola totalmente comprimida para perfeita simetria de amortecimento.",
    isWearItem: true,
  },
  {
    id: "oem-amortecedor-esq-52400",
    partNumber: "52400-K0W-N01",
    name: "Amortecedor Traseiro Showa Piggyback Esquerdo (Curso 120 mm)",
    nameEn: "Rear Left Shock Absorber Showa Piggyback",
    subsystem: "suspensao",
    categoryLabel: "Suspensão Traseira",
    hotspotId: "hotspot-susp-tras",
    explodedViewId: "suspensao-showa-piggyback.svg",
    calloutBalloonNumber: 1,
    description:
      "Amortecedor pressurizado a gás nitrogênio em reservatório externo dourado com mola de duplo estágio.",
    nominalDimensionMm: 120.0,
    serviceLimitMm: 120.0,
    torqueSpec: "Superior M10: 40 N.m | Inferior M8: 27 N.m",
    requiredTool: "Soquetes 14 mm e 12 mm + Torquímetro",
    criticalGotcha:
      "UNIDADE SELADA DE FÁBRICA: Proibido furar ou recarregar a câmara de nitrogênio. Troca se houver vazamento de óleo.",
    isWearItem: false,
  },
  {
    id: "oem-amortecedor-dir-52500",
    partNumber: "52500-K0W-N01",
    name: "Amortecedor Traseiro Showa Piggyback Direito (Curso 120 mm)",
    nameEn: "Rear Right Shock Absorber Showa Piggyback",
    subsystem: "suspensao",
    categoryLabel: "Suspensão Traseira",
    hotspotId: "hotspot-susp-tras",
    explodedViewId: "suspensao-showa-piggyback.svg",
    calloutBalloonNumber: 2,
    description:
      "Amortecedor traseiro Showa de alto desempenho com reservatório subtanque piggyback a gás nitrogênio.",
    nominalDimensionMm: 120.0,
    serviceLimitMm: 120.0,
    torqueSpec: "Superior M10: 40 N.m | Inferior M8: 27 N.m",
    requiredTool: "Soquetes 14 mm e 12 mm",
    criticalGotcha:
      "Rosquear parafusos à mão com os coxins alinhados para não cisalhar a rosca de alumínio da carcaça.",
    isWearItem: false,
  },
  {
    id: "oem-eixo-diant-90305",
    partNumber: "90305-K58-TC1",
    name: "Porca do Eixo da Roda Dianteira M12 (Sext. 19 mm)",
    nameEn: "Front Axle Nut M12",
    subsystem: "suspensao",
    categoryLabel: "Eixos e Rodas",
    hotspotId: "hotspot-susp-diant",
    explodedViewId: "suspensao-garfo-showa-31mm.svg",
    calloutBalloonNumber: 4,
    description:
      "Porca autoblocante flangeada para fixação do eixo passante de 12 mm da roda dianteira.",
    torqueSpec: "59 N.m (6,0 kgf.m)",
    requiredTool: "Soquetes 17 mm e 19 mm + Torquímetro",
    criticalGotcha: "Apoiar a cabeça do eixo oposta contra rotação ao aplicar os 59 N.m de aperto.",
    isWearItem: false,
  },
  {
    id: "oem-eixo-tras-90305",
    partNumber: "90305-KVB-901",
    name: "Porca Castelo do Eixo Traseiro M16 (Sext. 24 mm)",
    nameEn: "Rear Axle Nut M16 (Castle Nut)",
    subsystem: "suspensao",
    categoryLabel: "Eixos e Rodas",
    hotspotId: "hotspot-susp-tras",
    explodedViewId: "redutor-oleo-bujoes.svg",
    calloutBalloonNumber: 3,
    description:
      "Porca castelo de alta classe de resistência com travamento por contrapino de aço galvanizado.",
    torqueSpec: "118 N.m (12,0 kgf.m)",
    requiredTool: "Soquete de impacto 24 mm + Torquímetro de alta escala",
    criticalGotcha:
      "Travar a manete de freio traseiro durante o torque. Instalar OBRIGATORIAMENTE contrapino novo.",
    isWearItem: false,
  },

  // ==========================================
  // GRUPO 6: CHASSI, BAÚ & CARENAGENS
  // ==========================================
  {
    id: "oem-chassi-50100",
    partNumber: "50100-K0W-N00",
    name: "Chassi Berço Duplo Monobloco Tubular em Aço",
    nameEn: "Main Frame Body Comp.",
    subsystem: "chassi",
    categoryLabel: "Estrutura Principal",
    hotspotId: "hotspot-eletrica-bateria",
    explodedViewId: "chassi-pushpins-carenagens.svg",
    calloutBalloonNumber: 1,
    description:
      "Estrutura tubular de alta rigidez torcional em aço carbono com vão livre do solo reforçado de 165 mm.",
    torqueSpec: "Pontos de montagem conformados",
    requiredTool: "Inspeção visual e alinhamento",
    criticalGotcha:
      "Inspecionar soldas do berço inferior contra impactos de pedras em estradas não pavimentadas.",
    isWearItem: false,
  },
  {
    id: "oem-presilha-90683",
    partNumber: "90683-GAZ-003",
    name: "Presilhas Plásticas de Expansão Rápida 6 mm (Push-Pins)",
    nameEn: "Plastic Push-Pin Clip 6mm",
    subsystem: "chassi",
    categoryLabel: "Fixadores de Carenagem",
    hotspotId: "hotspot-filtro-ar",
    explodedViewId: "chassi-pushpins-carenagens.svg",
    calloutBalloonNumber: 2,
    description:
      "Presilhas de três estágios de engate rápido utilizadas nas carenagens plásticas e túnel central.",
    nominalDimensionMm: 6.0,
    serviceLimitMm: 6.0,
    torqueSpec: "Encaixe com clique",
    requiredTool: "Chave fenda fina ou punção suave",
    criticalGotcha:
      "MECANISMO DE 3 ESTÁGIOS: Para retirar, afundar o centro 2 mm (clique). Para armar, puxar o pino 4 mm para fora. Nunca puxar à força.",
    isWearItem: true,
  },
  {
    id: "oem-bau-81250",
    partNumber: "81250-K0W-N00",
    name: "Caixa de Bagagem sob o Assento (Luggage Box 27 Litros)",
    nameEn: "Luggage Box (27L)",
    subsystem: "chassi",
    categoryLabel: "Compartimentos",
    hotspotId: "hotspot-eletrica-bateria",
    explodedViewId: "chassi-pushpins-carenagens.svg",
    calloutBalloonNumber: 3,
    description:
      "Compartimento volumoso de 27 litros moldado em polímero resistente com vedação de borracha perimetral.",
    torqueSpec: "10 N.m (1,0 kgf.m)",
    requiredTool: "Soquete 10 mm longo + 2 presilhas dianteiras",
    criticalGotcha:
      "4 parafusos flangeados no fundo; retirar para acesso total ao cabeçote, vela e bico injetor.",
    isWearItem: false,
  },

  // ==========================================
  // GRUPO 7: ELÉTRICA, ILUMINAÇÃO & SMART KEY
  // ==========================================
  {
    id: "oem-bateria-31500",
    partNumber: "31500-KZZ-D01",
    name: "Bateria Selada Regulada por Válvula VRLA 12V 5Ah (YTZ6V)",
    nameEn: "VRLA Battery 12V 5Ah (Yuasa YTZ6V)",
    subsystem: "eletrica",
    categoryLabel: "Sistema Elétrico",
    hotspotId: "hotspot-eletrica-bateria",
    explodedViewId: "eletrica-bateria-ytz6v-smartkey.svg",
    calloutBalloonNumber: 1,
    description:
      "Bateria selada AGM livre de manutenção de alto CCA instalada no compartimento estanque sob o banco.",
    nominalDimensionMm: 12.8,
    serviceLimitMm: 12.4,
    torqueSpec: "Bornes: aperto manual firme",
    requiredTool: "Voltímetro digital / Testador de condutância",
    criticalGotcha:
      "Tensão em repouso ≥ 12,8V (100%). Se cair para menos de 10,2V na partida, trocar para não queimar o estator ACG.",
    isWearItem: true,
  },
  {
    id: "oem-smartkey-fob-35111",
    partNumber: "35111-K0W-N01",
    name: "Controle Transmissor FOB Honda Smart Key",
    nameEn: "Smart Key FOB Transmitter",
    subsystem: "eletrica",
    categoryLabel: "Chave Presencial",
    hotspotId: "hotspot-eletrica-bateria",
    explodedViewId: "eletrica-bateria-ytz6v-smartkey.svg",
    calloutBalloonNumber: 2,
    description:
      "Chave remota presencial de rádio com botão de busca, alarme antifurto e transponder criptografado.",
    torqueSpec: "Encaixe estanque com O-ring",
    requiredTool: "Espátula plástica para abertura",
    criticalGotcha:
      "Preservar a borracha de vedação ao trocar a bateria moeda. Não usar lâminas de aço afiadas.",
    isWearItem: false,
  },
  {
    id: "oem-bateria-cr2032",
    partNumber: "CR2032",
    name: "Bateria Moeda de Lítio CR2032 (3V) da Smart Key",
    nameEn: "Lithium Coin Battery CR2032 3V",
    subsystem: "eletrica",
    categoryLabel: "Chave Presencial",
    hotspotId: "hotspot-eletrica-bateria",
    explodedViewId: "eletrica-bateria-ytz6v-smartkey.svg",
    calloutBalloonNumber: 3,
    description:
      "Elemento de alimentação de lítio de 3,0 Volts para o circuito transmissor da chave de presença.",
    nominalDimensionMm: 3.0,
    serviceLimitMm: 2.7,
    torqueSpec: "Encaixe elástico",
    requiredTool: "Troca anual preventiva",
    criticalGotcha:
      "POLARIDADE MANDATÓRIA: Polo Positivo (+) voltado para baixo no compartimento da chave.",
    isWearItem: true,
  },
  {
    id: "oem-estator-acg-31120",
    partNumber: "31120-K0W-N01",
    name: "Estator do Alternador de Partida Silenciosa ACG",
    nameEn: "ACG Starter / Generator Stator",
    subsystem: "eletrica",
    categoryLabel: "Geração e Partida",
    hotspotId: "hotspot-motor-radiador",
    explodedViewId: "eletrica-bateria-ytz6v-smartkey.svg",
    calloutBalloonNumber: 4,
    description:
      "Motor-gerador trifásico sem escovas integrado ao virabrequim com função reversa Swing-Back.",
    torqueSpec: "10 N.m (1,0 kgf.m)",
    requiredTool: "Sacador do rotor do alternador",
    criticalGotcha:
      "Elimina motor de arranque e bendix mecânico. Resistência idêntica entre as 3 fases.",
    isWearItem: false,
  },

  // ==========================================
  // GRUPO 8: CONSUMÍVEIS & FERRAMENTAL
  // ==========================================
  {
    id: "oem-filtro-ar-17210",
    partNumber: "17210-K0W-B00",
    name: "Elemento do Filtro de Ar do Motor (Papel Viscoso)",
    nameEn: "Air Cleaner Element (Viscous Paper)",
    subsystem: "consumiveis",
    categoryLabel: "Filtragem de Ar",
    hotspotId: "hotspot-filtro-ar",
    explodedViewId: "motor-filtro-ar-viscoso-17210-k0w-b00.svg",
    calloutBalloonNumber: 1,
    description:
      "Filtro de celulose com impregnação química de óleo de alta retenção micrométrica.",
    torqueSpec: "Parafusos tampa: 1,5 N.m",
    requiredTool: "Troca compulsória a 18.000 km",
    criticalGotcha:
      "PROIBIÇÃO ABSOLUTA: NUNCA soprar com ar comprimido. O sopro abre as fibras e destrói o cilindro com poeira abrasiva.",
    isWearItem: true,
  },
  {
    id: "oem-filtro-cvt-17274",
    partNumber: "17274-K0W-N00",
    name: "Espuma do Duto de Arrefecimento do CVT",
    nameEn: "CVT Duct Foam Filter",
    subsystem: "consumiveis",
    categoryLabel: "Filtragem de Ar",
    hotspotId: "hotspot-filtro-ar",
    explodedViewId: "motor-filtro-ar-viscoso-17210-k0w-b00.svg",
    calloutBalloonNumber: 2,
    description:
      "Espuma de poliuretano lavável na tomada de ar frontal da carcaça do variador primário.",
    torqueSpec: "Instalação manual",
    requiredTool: "Água morna e sabão neutro",
    criticalGotcha:
      "Lavar e secar à sombra. Instalar 100% a seco (nunca aplicar óleo ou desmoldante).",
    isWearItem: true,
  },
  {
    id: "oem-oleo-pro-08232",
    partNumber: "08232-M99-K1JT1",
    name: "Óleo Genuíno Pro Honda 4T Scooter SAE 10W-30 API SL JASO MB",
    nameEn: "Pro Honda 10W-30 JASO MB Oil",
    subsystem: "consumiveis",
    categoryLabel: "Lubrificantes Oficiais",
    hotspotId: "hotspot-motor-carter",
    explodedViewId: "motor-tela-oleo-bujoes.svg",
    calloutBalloonNumber: 7,
    description:
      "Lubrificante com modificadores de atrito específicos para motores eSP com embreagem centrífuga a seco.",
    nominalDimensionMm: 800.0,
    serviceLimitMm: 800.0,
    torqueSpec: "800 ml no dreno / 900 ml na desmontagem",
    requiredTool: "Funil limpo / medidor graduado",
    criticalGotcha:
      "PROIBIDO ÓLEO JASO MA/MA2: Usar óleo de moto comum aumenta o consumo, eleva a temperatura em 10°C e rouba 5% de potência.",
    isWearItem: true,
  },
  {
    id: "oem-coolant-pro-08cla",
    partNumber: "08CLA-G01-020",
    name: "Líquido de Arrefecimento Pro Coolant Tipo 2 (OAT 50/50)",
    nameEn: "Honda Pro Coolant Type 2 (OAT 50/50)",
    subsystem: "consumiveis",
    categoryLabel: "Fluidos Oficiais",
    hotspotId: "hotspot-motor-radiador",
    explodedViewId: "arrefecimento-radiador-tampa-108kpa.svg",
    calloutBalloonNumber: 7,
    description:
      "Fluido orgânico pronto para uso isento de silicatos e boratos (capacidade: 480 ml radiador + 130 ml expansor).",
    nominalDimensionMm: 610.0,
    serviceLimitMm: 610.0,
    torqueSpec: "Troca a cada 36.000 km",
    requiredTool: "Drenagem com purga de ar ativa",
    criticalGotcha:
      "Isenção total de silicatos protege o selo mecânico cerâmico carbeto de silício da bomba d'água.",
    isWearItem: true,
  },
  {
    id: "oem-fluido-freio-08203",
    partNumber: "08203-0004",
    name: "Fluido de Freio Genuíno Honda DOT 4 (Alto Ponto de Ebulição)",
    nameEn: "Genuine Honda Brake Fluid DOT 4",
    subsystem: "consumiveis",
    categoryLabel: "Fluidos Oficiais",
    hotspotId: "hotspot-freio-diant",
    explodedViewId: "freio-abs-circuito-blindagem.svg",
    calloutBalloonNumber: 4,
    description:
      "Fluido hidráulico sintético glicol de alto ponto de ebulição a seco (> 260°C) homologado para sistemas ABS Nissin.",
    nominalDimensionMm: 250.0,
    serviceLimitMm: 250.0,
    torqueSpec: "Troca a cada 2 anos / 24.000 km",
    requiredTool: "Sangria contínua com seringa e mangueira 3/16 pol",
    criticalGotcha:
      "MANDATÓRIO: Utilizar fluido DOT 4 novo de frasco lacrado. Fluido contaminado por umidade abaixa o ponto de ebulição e cria vapor lock em descidas de serra.",
    isWearItem: true,
  },
  {
    id: "tool-chave-jis-vessel",
    partNumber: "JIS B 1012 (Vessel #2)",
    name: "Chave Cruzada Japonesa JIS #2 Anti-Cam-Out (Vessel Megadora)",
    nameEn: "JIS Japanese Screwdriver #2",
    subsystem: "consumiveis",
    categoryLabel: "Ferramental de Precisão",
    hotspotId: "hotspot-freio-diant",
    explodedViewId: "ferramentas-jis-vessel-vs-phillips.svg",
    calloutBalloonNumber: 1,
    description:
      "Chave cruzada com paredes verticais a 90 graus e ponta chata desenvolvida para parafusos com dot-mark japonês.",
    torqueSpec: "1,5 N.m nos reservatórios Nissin",
    requiredTool: "Padrão oficial japonês",
    criticalGotcha:
      "PROIBIDO USAR CHAVE PHILLIPS PH2 DIN/ISO: A chave Phillips comum tem ângulo de 57° que empurra a ponta para fora (cam-out) e espana a tampa Nissin.",
    isWearItem: false,
  },
  {
    id: "tool-fixador-y-07725",
    partNumber: "07725-0030000",
    name: "Fixador Universal de Polia em Y Honda (Universal Holder)",
    nameEn: "Universal Pulley Holder",
    subsystem: "consumiveis",
    categoryLabel: "Ferramental Especial",
    hotspotId: "hotspot-cvt-primario",
    explodedViewId: "ferramentas-honda-especiais-y-compressor.svg",
    calloutBalloonNumber: 1,
    description:
      "Chave em Y com pinos de aço temperado reguláveis para imobilizar o ventilador primário e a campana secundária.",
    torqueSpec: "Imobilização a 59 N.m e 49 N.m",
    requiredTool: "Ferramenta especial oficial Honda",
    criticalGotcha:
      "PROIBIDO APOIAR NAS ALETAS: Quebrar uma aleta de 5g gera desbalanceamento dinâmico de 19,4 kgf a 8.500 rpm, destruindo os mancais.",
    isWearItem: false,
  },
  {
    id: "tool-compressor-07lme",
    partNumber: "07LME-GZ40201",
    name: "Compressor de Mola da Polia Secundária com Prensa Rosqueada",
    nameEn: "Clutch Spring Compressor",
    subsystem: "consumiveis",
    categoryLabel: "Ferramental Especial",
    hotspotId: "hotspot-cvt-secundario",
    explodedViewId: "ferramentas-honda-especiais-y-compressor.svg",
    calloutBalloonNumber: 2,
    description:
      "Prensa mecânica roscada com fuso central para retenção de segurança da contra-mola de 35 a 50 kgf.",
    torqueSpec: "Contenção de segurança",
    requiredTool: "Dispositivo de prensa de bancada",
    criticalGotcha:
      "Elimina o risco balístico de ejeção violenta da porca de 39 mm ao descomprimir as sapatas.",
    isWearItem: false,
  },
  {
    id: "tool-soquete-39mm-07gma",
    partNumber: "07GMA-KS40100",
    name: "Soquete Plano Especial sem Chanfro 39 mm (Rosca M28)",
    nameEn: "Clutch Nut Socket 39mm (Flat Face)",
    subsystem: "consumiveis",
    categoryLabel: "Ferramental Especial",
    hotspotId: "hotspot-cvt-secundario",
    explodedViewId: "ferramentas-honda-especiais-y-compressor.svg",
    calloutBalloonNumber: 3,
    description:
      "Soquete sextavado com face usinada totalmente plana sem chanfro de entrada para porcas de baixo perfil de 4 mm.",
    torqueSpec: "54 N.m (5,5 kgf.m)",
    requiredTool: "Usinagem plana retificada",
    criticalGotcha:
      "Distribui 100% da área de contato sobre a porca fina M28, evitando que a ferramenta escorregue e arredonde os cantos.",
    isWearItem: false,
  },
];
